/**
 * Medical On Demand Helper Utilities
 *
 * Utility functions for MOD-specific logic including message formatting.
 */

import {
  ALL_PRODUCTS,
  getProductData,
  getProductSupportTeam,
  getQuestionTypeLabel,
  getSupportTeamByProductId,
  MOD_ROUTE_IDS,
  MOD_ROUTES,
  type ModRoute,
  type Product,
  type ProductId,
} from '@/constants/medicalOnDemand';
import type { InquiryV2 } from '@/services/getInquiriesV2';
import { getAvatarUrl } from '@/util/getAvatarUrl';
import type { InquiryGroup, InquiryGroupExpert } from '@/util/inquiryGroups';

/**
 * Format a MOD message with product and question type prefix
 *
 * Prepends the product name and question type category to the message in a user-friendly format
 * that is readable in the conversation while still being structured for analytics.
 *
 * @example
 * formatMODMessage('pharma-product-1', 'clinical-trials', 'What are the latest results?')
 * // Returns: "Product: PRODUCT 1\nQuestion Type: Clinical Trials & Study Data\n\nWhat are the latest results?"
 */
export const formatMODMessage = (
  productIds: ProductId[],
  questionTypeId: string | undefined,
  message: string
): string => {
  const brandNames = productIds.map((id) => getProductData(id)?.brandName ?? id);

  const productLine =
    brandNames.length === 1 ? `Product: ${brandNames[0]}` : `Products: ${brandNames.join(' · ')}`;

  if (!questionTypeId) {
    return `${productLine}\n\n${message}`;
  }

  const questionTypeLabel = getQuestionTypeLabel(questionTypeId);

  return `${productLine}\nQuestion Type: ${questionTypeLabel}\n\n${message}`;
};

/**
 * Strip MOD prefix from a message to get just the user's content
 *
 * MOD messages may have the following formats:
 * - "Product: BRAND\n\nActual message" (without question type)
 * - "Product: BRAND\nQuestion Type: LABEL\n\nActual message" (with question type)
 *
 * This function removes everything before the blank line separator.
 * Returns the original message if no MOD prefix is found.
 *
 * @example
 * stripProductInfoPrefix("Product: TALVEY®\nQuestion Type: Clinical Trials\n\nMy question")
 * // Returns: "My question"
 *
 * @example
 * stripProductInfoPrefix("Product: TALVEY®\n\nMy question without question type")
 * // Returns: "My question without question type"
 */
export const stripProductInfoPrefix = (message: string | undefined): string => {
  if (!message) {
    return '';
  }

  if (!message.startsWith('Product:') && !message.startsWith('Products:')) {
    return message;
  }

  const separatorIndex = message.indexOf('\n\n');

  if (separatorIndex === -1) {
    return message;
  }

  return message.substring(separatorIndex + 2);
};

/**
 * Extract product information from a MOD formatted message
 *
 * Parses the "Product: BRAND_NAME" or "Products: BRAND_A · BRAND_B" prefix from a MOD message.
 *
 * @example
 * extractProductsFromMessage("Product: TALVEY®\nQuestion Type: Clinical Trials\n\nMy question")
 * // Returns: [{ id: 'talvey', brandName: 'TALVEY®', ... }]
 *
 * @example
 * extractProductsFromMessage("Products: TALVEY® · TECVAYLI®\n\nMy question")
 * // Returns: [{ id: 'talvey', ... }, { id: 'tecvayli', ... }]
 *
 * @example
 * extractProductsFromMessage("Hello, this is a regular message")
 * // Returns: []
 */
export const extractProductsFromMessage = (message: string | undefined): Product[] => {
  if (!message) {
    return [];
  }

  const productMatch = message.match(/^Products?:\s*(.+?)(?:\n|$)/);

  if (!productMatch) {
    return [];
  }

  const brandNames = productMatch[1].split('·').map((s) => s.trim());

  const findProduct = (brandName: string) =>
    ALL_PRODUCTS.find(
      (product) =>
        product.brandName === brandName ||
        product.brandName.replace('®', '') === brandName.replace('®', '')
    );

  return brandNames.map(findProduct).filter((p): p is Product => p != null);
};

/** @deprecated Use extractProductsFromMessage instead */
export const extractProductFromMessage = (message: string | undefined): Product | undefined => {
  return extractProductsFromMessage(message)[0];
};

export type ProductDisplayInfo = {
  productInfo: Product;
  products: Product[];
  displayName: string;
  displaySubject: string;
  productLabel: string;
};

/**
 * Build product display info from resolved Product objects.
 */
const buildProductDisplayInfo = (products: Product[]): ProductDisplayInfo | null => {
  if (products.length === 0) {
    return null;
  }

  const pharmaTeam = getSupportTeamByProductId(products[0].id);

  if (!pharmaTeam) {
    return null;
  }

  const productLabel = products.map((p) => `${p.brandName} (${p.genericName})`).join(' · ');

  return {
    productInfo: products[0],
    products,
    displayName: pharmaTeam.name,
    displaySubject: pharmaTeam.specialty,
    productLabel,
  };
};

/**
 * Get product display information from structured product IDs (message attributes).
 */
export const getProductDisplayInfoFromIds = (
  productIds: string[] | undefined
): ProductDisplayInfo | null => {
  if (!productIds?.length) {
    return null;
  }

  const products = productIds
    .map((id) => getProductData(id as ProductId))
    .filter((p): p is Product => p != null);

  return buildProductDisplayInfo(products);
};

/**
 * Get product display information from a MOD message text prefix.
 *
 * @example
 * getProductDisplayInfo("Product: TALVEY®\n\nQuestion")
 * // Returns: { productLabel: 'Product: TALVEY® (talquetamab-tgvs)', ... }
 */
export const getProductDisplayInfo = (message: string | undefined): ProductDisplayInfo | null => {
  const products = extractProductsFromMessage(message);

  return buildProductDisplayInfo(products);
};

export type ModDisplayInfo = {
  displayName: string;
  displayNameWithoutDoctor: string;
  displaySubject: string;
  displayMessage: string;
  productLabel?: string;
  productInfo?: Product;
  products?: Product[];
  isMod: boolean;
};

/**
 * Get MOD display info for an inbox card using layered resolution.
 * Only returns MOD display when the other participant IS the MOD expert:
 * 1. Verify participant is a known MOD expert by ID
 * 2. Try structured productIds from message attributes (if available)
 * 3. Try message-based product extraction for product-specific detail
 * 4. Fall back to team-level display (name + specialty, no product label)
 */
export const getModDisplayForInbox = (
  participantId: string | number,
  lastMessagePreview?: string | null,
  productIds?: string[]
): ModDisplayInfo | null => {
  const pharmaTeam = getProductSupportTeam(participantId.toString());

  if (!pharmaTeam) {
    return null;
  }

  const message = lastMessagePreview ?? '';

  const productDisplay = getProductDisplayInfoFromIds(productIds) ?? getProductDisplayInfo(message);

  if (productDisplay) {
    return {
      displayName: productDisplay.displayName,
      displayNameWithoutDoctor: productDisplay.displayName,
      displaySubject: productDisplay.displaySubject,
      displayMessage: stripProductInfoPrefix(message),
      productLabel: productDisplay.productLabel,
      productInfo: productDisplay.productInfo,
      products: productDisplay.products,
      isMod: true,
    };
  }

  return {
    displayName: pharmaTeam.name,
    displayNameWithoutDoctor: pharmaTeam.name,
    displaySubject: pharmaTeam.specialty,
    displayMessage: message,
    isMod: true,
  };
};

/**
 * Reads the MOD Direct-to-MSL route from an inquiry's `title` payload
 * (mirrors Twilio conversation attributes, written by the BE per child
 * inquiry on fan-out). Returns `null` for legacy inquiries that pre-date
 * the route field.
 */
export const getInquiryRoute = (
  inquiry: { title?: { route?: ModRoute } | null } | null | undefined
): ModRoute | null => {
  return inquiry?.title?.route ?? null;
};

/** User-facing label for a MOD route (used in inbox cards + chat tabs). */
export const getModRouteLabel = (route: ModRoute): string => {
  return MOD_ROUTES.find((entry) => entry.id === route)?.label ?? '';
};

/**
 * Resolves the route label to surface on a single inquiry's inbox card.
 * Reads the explicit `title.route` first, then falls back to the only route
 * entry on `groupExperts` (set by `buildModRouteGroupExperts`) for cases
 * where the inquiry data on the inbox-list response is missing the field.
 * Returns `null` for non-MOD inquiries or multi-route groups.
 */
export function resolveModCardRouteLabel(
  inquiry: Parameters<typeof getInquiryRoute>[0],
  groupExperts: { name: string; variant?: 'route' }[] | undefined
): string | null {
  const explicitRoute = getInquiryRoute(inquiry);

  if (explicitRoute) {
    return getModRouteLabel(explicitRoute);
  }

  const routeEntries = (groupExperts ?? []).filter((expert) => expert.variant === 'route');

  return routeEntries.length === 1 ? routeEntries[0].name : null;
}

type RoutableInquiry = {
  conversationId: string;
  recipient: { id: number };
  title?: { route?: ModRoute } | null;
};

/**
 * Resolves the route for each inquiry in a sibling group, including
 * fallbacks for MOD fan-out groups where one child is missing `title.route`.
 *
 * Strategy:
 *   1. Pick up explicit `title.route` for each sibling.
 *   2. If the group is a MOD fan-out — no more siblings than there are
 *      routes, all share one recipient, AND at least one sibling carries an
 *      explicit route — fill a missing route by elimination, but only when
 *      exactly one route and exactly one sibling are unaccounted for.
 *   3. Otherwise, only honor explicit routes; siblings without one return
 *      `undefined`, letting callers fall back to default participant
 *      labeling.
 *
 * The explicit-route requirement in step 2 is load-bearing: an anonymous
 * (Ask Anonymously) 2-expert fan-out has the same shape as a MOD fan-out —
 * two siblings all sharing the anonymous routing recipient — but carries no
 * `title.route`. Without demanding real route evidence, elimination would
 * fabricate routes and mislabel every anonymous pair as J&J MOD.
 * The backend stamps `title.route` on genuine MOD children, so a real MOD
 * fan-out always has at least one routed sibling to key off.
 *
 * Elimination only resolves a single unknown. Routes are multi-select, so a
 * fan-out is not always the full set, and two unknowns against two remaining
 * routes is a coin toss. Naming the wrong J&J team on a real conversation is
 * worse than naming none, so an ambiguous sibling returns `undefined` and the
 * caller falls back to default participant labeling.
 *
 * Returns a `Map` keyed by `conversationId`. Stable across renders for the
 * same input.
 */
export function resolveModGroupRoutes<T extends RoutableInquiry>(
  siblings: readonly T[]
): Map<string, ModRoute> {
  const assignment = new Map<string, ModRoute>();

  if (siblings.length === 0) {
    return assignment;
  }

  const sharedRecipientId = siblings[0]?.recipient?.id;

  const allSiblingsShareRecipient =
    sharedRecipientId !== undefined &&
    siblings.every((sibling) => sibling.recipient?.id === sharedRecipientId);

  const hasModShape = siblings.length <= MOD_ROUTE_IDS.length && allSiblingsShareRecipient;

  const taken = new Set<ModRoute>();

  for (const sibling of siblings) {
    const explicit = getInquiryRoute(sibling);

    if (explicit) {
      assignment.set(sibling.conversationId, explicit);

      taken.add(explicit);
    }
  }

  const isModFanOutGroup = hasModShape && taken.size > 0;

  if (!isModFanOutGroup) {
    return assignment;
  }

  const remaining = MOD_ROUTE_IDS.filter((id) => !taken.has(id));

  // Sort unassigned siblings deterministically so the fill-by-elimination loop
  // always maps routes in the same order. Legacy inquiries lack `title.route`,
  // so without this sort the MSL / Med-Info labels could swap between renders.
  const unassigned = [...siblings]
    .filter((sibling) => !assignment.has(sibling.conversationId))
    .sort(
      (a, b) => a.recipient.id - b.recipient.id || a.conversationId.localeCompare(b.conversationId)
    );

  if (remaining.length === 1 && unassigned.length === 1) {
    const sibling = unassigned[0];

    const fill = remaining[0];

    if (sibling && fill) {
      assignment.set(sibling.conversationId, fill);
    }
  }

  return assignment;
}

/**
 * True when the viewer is the (shared) recipient of every inquiry in a
 * grouped MOD fan-out. Used to suppress route-name avatar entries on the
 * recipient inbox, where the submitter's profile should surface instead.
 */
const isViewerModGroupRecipient = (group: InquiryGroup, userId?: string): boolean => {
  if (userId === undefined) {
    return false;
  }

  const intUserId = Number(userId);

  return group.inquiries.every((inquiry) => inquiry.recipient.id === intUserId);
};

/**
 * MOD Direct-to-MSL fan-out predicate. Both sibling inquiries (MSL +
 * Med Info) share the same recipient (the MOD expert account). The
 * default "recipients see one row per conversation" rule was built for
 * LMP→multi-expert where each expert is a different recipient. For MOD
 * fan-out the recipient is the same user across siblings, so the
 * recipient view should also see the group merged into one inbox row.
 */
export const isModRouteRecipient = (inquiry: InquiryV2, userId?: string): boolean => {
  if (!userId) {
    return false;
  }

  if (inquiry.recipient.id !== Number(userId)) {
    return false;
  }

  return Boolean(inquiry.title?.route);
};

/**
 * For the MOD recipient (J&J Medical Information account) viewing their
 * inbox, derive a flat list of route labels so the card can render them
 * as small chips alongside the submitter's name+avatar. Returns an empty
 * array when the group isn't a MOD route group, the viewer isn't the
 * recipient, or there's no userId.
 */
export function getModRouteLabelsForGroup(group: InquiryGroup, userId?: string): string[] {
  if (!isViewerModGroupRecipient(group, userId)) {
    return [];
  }

  const assignment = resolveModGroupRoutes(group.inquiries);

  if (assignment.size === 0) {
    return [];
  }

  const present = new Set(assignment.values());

  return MOD_ROUTE_IDS.filter((route) => present.has(route)).map(getModRouteLabel);
}

/**
 * Builds the inbox-card group-expert chip entries for a MOD fan-out group.
 * Returns `null` when the group isn't a MOD route group OR when the viewer
 * is the MOD recipient. In both cases the caller falls back to the standard
 * participant-based expert list — for the recipient, the submitter's
 * name+avatar is surfaced normally and routes are shown as a separate chip
 * line via `getModRouteLabelsForGroup`.
 *
 * Resolves routes via `resolveModGroupRoutes`, deduplicates by route, and
 * pulls a shared avatar from the recipient (the MOD expert's J&J brand
 * mark) — relevant for the submitter's view.
 */
export function buildModRouteGroupExperts(
  group: InquiryGroup,
  userId?: string
): InquiryGroupExpert[] | null {
  const assignment = resolveModGroupRoutes(group.inquiries);

  if (assignment.size === 0) {
    return null;
  }

  if (isViewerModGroupRecipient(group, userId)) {
    return null;
  }

  const sharedAvatarUrl = getAvatarUrl(group.inquiries[0]?.recipient.avatar);

  const present = new Set(assignment.values());

  return MOD_ROUTE_IDS.filter((route) => present.has(route)).map((route) => ({
    name: getModRouteLabel(route),
    avatarUrl: sharedAvatarUrl,
    variant: 'route' as const,
  }));
}
